import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import * as Router from "react-router-dom";
import moment, { Moment } from "moment";
import { AppDatePicker } from "component/shared/date-picker";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { FeatherIcon } from "component/shared/feather-icon";
import { ColdFusionIframe } from "component/shared/iframe/coldfusion";
import { RouterLinkWrapper } from "component/shared/router-link-wrapper";
import { Table, Column, Sort } from "component/shared/table";
import { parseDateParam } from "component/shared/report-filter-params";
import { TablePagination } from "component/shared/table/table-pagination";
import { PageProps } from "shared/page-props";
import { RootState } from "redux/store";
import {
	getSavedSearchActivityRows,
	getSavedSearchActivityTotalCount,
	getSavedSearchActivityLoading,
	getSavedSearchActivityLoaded,
} from "redux/selector";
import { fetchSavedSearchActivity } from "redux/slice/saved-search-activity";
import { SavedSearchActivityRowResource, SavedSearchActivityQuery } from "api/resource/saved-search-activity";
import { coldFusionReplatform } from "shared/env";
import { styles } from "./style";

interface Props extends PageProps, Router.RouteComponentProps, WithStyles<typeof styles> {}

const TITLE = "Saved Search Activity";
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [5, 10, 20, 25, 50, 100];

const formatShortDate = (iso: string): string => (iso ? moment(iso).format("M/D/YY") : "");

// Column id -> server sortBy. Only these columns are server-sortable (SearchProfileDao whitelist:
// date / name / email); cities/zip/price have no server sort, matching CF.
const SORT_COLUMN_TO_SERVER: Record<string, "date" | "name"> = { dateSaved: "date", name: "name" };

// CF price rule (StatsSavedSearches1.cfm:152-161): < 0 sentinels (-2 min / -1 max) → No Min / No Max.
// CF read the raw query column, so it saw those sentinels. We don't: SearchProfile.getMinListPrice()
// normalizes anything <= 0 to null, and SavedSearchActivityRowResource is @JsonInclude(NON_NULL), so an
// unbounded side arrives as undefined (field omitted) — never as -2/-1. Guard null/undefined first;
// `undefined < 0` is false, which is what crashed the page (MAX-4158). The < 0 arm is kept in case a
// raw sentinel ever reaches the wire. Note 0 also normalizes to null upstream, and $0 min == "No Min".
const formatPriceRange = (min?: number | null, max?: number | null): string => {
	const minStr = min == null || min < 0 ? "No Min" : `$${min.toLocaleString()}`;
	const maxStr = max == null || max < 0 ? "No Max" : `$${max.toLocaleString()}`;
	return `${minStr} - ${maxStr}`;
};

// CF cf_search_city_list truncates the resolved city-name list to 60 chars + "..." (search_city_list.cfm:24-29).
const truncateCities = (cities: string | null): string => {
	if (!cities) {
		return "";
	}
	return cities.length > 60 ? `${cities.substring(0, 60)}...` : cities;
};

const ColdFusionFallback: React.FC<Props> = ({ user }) => (
	<DashboardLayout
		permitted={user && user.permissions.reporting}
		title={TITLE}
		header={
			<Mui.Typography variant="h1">
				<FeatherIcon>
					<Icons.BarChart2 />
				</FeatherIcon>
				{TITLE}
			</Mui.Typography>
		}
		iframe={<ColdFusionIframe url="/statsSavedSearches.cfm" />}
	/>
);

// CF StatsSavedSearches1.cfm:17-27 — the email-digest link deep-links into a dated report via
// ?startDate=&endDate=; parseDateParam (shared) reads them, and handleSubmit writes them back.
const ReportsSavedSearchesBase: React.FC<Props> = ({ user, classes = {}, location, history }) => {
	const dispatch = useDispatch();
	const rows = useSelector((state: RootState) => getSavedSearchActivityRows(state));
	const totalCount = useSelector((state: RootState) => getSavedSearchActivityTotalCount(state));
	const loading = useSelector((state: RootState) => getSavedSearchActivityLoading(state));
	const loaded = useSelector((state: RootState) => getSavedSearchActivityLoaded(state));

	const [startDate, setStartDate] = useState<Moment>(() => parseDateParam(location.search, "startDate") ?? moment().subtract(3, "days"));
	const [endDate, setEndDate] = useState<Moment>(() => parseDateParam(location.search, "endDate") ?? moment());
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [sort, setSort] = useState<Sort>({ id: "dateSaved", desc: true });
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: "" });

	const loadReport = React.useCallback(
		async (nextPage: number, nextSize: number, sortArg: Sort) => {
			const query: SavedSearchActivityQuery = {
				startDate: startDate.format("YYYY-MM-DD"),
				endDate: endDate.format("YYYY-MM-DD"),
				page: nextPage,
				pageSize: nextSize,
				sortBy: SORT_COLUMN_TO_SERVER[sortArg.id] ?? "date",
				sortDir: sortArg.desc ? "desc" : "asc",
			};
			try {
				// F4: .unwrap() so an API failure rejects here.
				await dispatch(fetchSavedSearchActivity(query) as any).unwrap();
			} catch{
				setSnackbar({ open: true, message: "Failed to load report" });
			}
		},
		[dispatch, startDate, endDate]
	);

	useEffect(() => {
		loadReport(0, DEFAULT_PAGE_SIZE, sort);
	}, [dispatch]); // eslint-disable-line

	const handleSubmit = () => {
		// Reflect the applied filters in the URL so they persist across back-nav / refresh / share
		// (spec-directed URL query params in place of CF's per-client session vars).
		history.replace({
			search: new URLSearchParams({
				startDate: startDate.format("YYYY-MM-DD"),
				endDate: endDate.format("YYYY-MM-DD"),
			}).toString(),
		});
		setPage(0);
		loadReport(0, pageSize, sort);
	};

	const handlePageChange = (nextPage: number) => {
		setPage(nextPage);
		loadReport(nextPage, pageSize, sort);
	};

	const handlePageSizeChange = (nextSize: number) => {
		setPageSize(nextSize);
		setPage(0);
		loadReport(0, nextSize, sort);
	};

	// Column-click sort — server-paged, so a header click refetches page 0 with the new sort rather
	// than only reordering the visible page.
	const handleSortChange = (nextSort: Sort) => {
		setSort(nextSort);
		setPage(0);
		loadReport(0, pageSize, nextSort);
	};

	const columns: Column<SavedSearchActivityRowResource>[] = [
		{
			id: "name",
			title: "Name",
			// Comparators keep the returned server page ordered consistently; the actual sort is server-side.
			sort: (a, b) => (a.leadName || "").localeCompare(b.leadName || ""),
			value: (row) => (
				<span data-testid={`saved-search-row-${row.savedSearchId}`}>
					<Mui.Typography variant="body2" className={classes.nameLabel}>
						{row.leadName}
					</Mui.Typography>
					<Mui.Typography variant="body2" color="textSecondary">
						{row.leadEmail}
					</Mui.Typography>
				</span>
			),
		},
		{
			id: "dateSaved",
			title: "Date Saved",
			sort: (a, b) => (a.createdOn || "").localeCompare(b.createdOn || ""),
			value: (row) => formatShortDate(row.createdOn),
		},
		{
			id: "searchDetails",
			title: "Search Details",
			value: (row) => (
				<Mui.Grid container direction="column" spacing={1}>
					{row.cities && (
						<Mui.Grid item>
							<Mui.Typography variant="body2">
								<span className={classes.detailLabel}>Cities: </span>
								{truncateCities(row.cities)}
							</Mui.Typography>
						</Mui.Grid>
					)}
					{row.zip && (
						<Mui.Grid item>
							<Mui.Typography variant="body2">
								<span className={classes.detailLabel}>Zip: </span>
								{row.zip}
							</Mui.Typography>
						</Mui.Grid>
					)}
					<Mui.Grid item>
						<Mui.Typography variant="body2">
							<span className={classes.detailLabel}>Price Range: </span>
							{formatPriceRange(row.minListPrice, row.maxListPrice)}
						</Mui.Typography>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.Button
							size="small"
							variant="outlined"
							component={RouterLinkWrapper}
							to={`/lead/${row.leadId}`}
							data-testid={`saved-search-view-${row.savedSearchId}`}
						>
							View Search
						</Mui.Button>
					</Mui.Grid>
				</Mui.Grid>
			),
		},
	];

	const renderResults = () => {
		if (loaded && rows.length === 0) {
			return (
				<MuiAlert severity="info">
					{/* CF format is mm/dd/yyyy (StatsSavedSearches1.cfm:33,173-179) — match it and the page's
						own date pickers (format="MM/dd/yyyy") rather than the terse M/D/YY. */}
					No Matches for Date Range. {startDate.format("MM/DD/YYYY")} - {endDate.format("MM/DD/YYYY")}
				</MuiAlert>
			);
		}
		return (
			<Mui.Grid container direction="column" spacing={2}>
				<Mui.Grid item>
					<Mui.Typography variant="h6">Saved Search Results ({totalCount})</Mui.Typography>
				</Mui.Grid>
				<Mui.Grid item>
					{/* Server-paged: render only the current page; the shared Table hardcodes
						count=items.length and always slices, so hidePagination + pageSize=row count
						(MAX-3876 F8) and a standalone TablePagination bound to totalCount below. */}
					<Table items={rows} columns={columns} hidePagination pageSize={Math.max(rows.length, 1)} sort={sort} onSortChange={handleSortChange} />
				</Mui.Grid>
				<Mui.Grid item>
					<TablePagination
						count={totalCount}
						page={page}
						rowsPerPage={pageSize}
						rowsPerPageOptions={PAGE_SIZE_OPTIONS}
						onPageChange={(_event, nextPage) => handlePageChange(nextPage)}
						onRowsPerPageChange={(event) => handlePageSizeChange(parseInt(event.target.value, 10))}
					/>
				</Mui.Grid>
			</Mui.Grid>
		);
	};

	return (
		<DashboardLayout
			permitted={user && user.permissions.reporting}
			title={TITLE}
			header={
				<Mui.Typography variant="h1">
					<FeatherIcon>
						<Icons.BarChart2 />
					</FeatherIcon>
					{TITLE}
				</Mui.Typography>
			}
		>
			<FullPageCard>
				<Mui.Grid container spacing={2} alignItems="flex-end" className={classes.filterBar}>
					<Mui.Grid item xs={6} sm={3} data-testid="saved-search-filter-start">
						<AppDatePicker
								format="MM/dd/yyyy"
								fullWidth
								label="Start Date"
								placeholder="mm/dd/yyyy"
								value={startDate.toDate()}
								onChange={(date) => {
									setStartDate(date ? moment(date) : moment());
									setPage(0);
								}}
								openPickerIcon={<Icons.Calendar size={20} />}
								views={["day"]}
							/>
					</Mui.Grid>
					<Mui.Grid item xs={6} sm={3} data-testid="saved-search-filter-end">
						<AppDatePicker
								format="MM/dd/yyyy"
								fullWidth
								label="End Date"
								placeholder="mm/dd/yyyy"
								value={endDate.toDate()}
								onChange={(date) => {
									setEndDate(date ? moment(date) : moment());
									setPage(0);
								}}
								openPickerIcon={<Icons.Calendar size={20} />}
								views={["day"]}
							/>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.Button
							variant="contained"
							color="primary"
							onClick={handleSubmit}
							data-testid="saved-search-filter-submit"
						>
							Submit
						</Mui.Button>
					</Mui.Grid>
				</Mui.Grid>

				{loading && !loaded ? <Mui.CircularProgress /> : renderResults()}
			</FullPageCard>

			<Mui.Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
			>
				<MuiAlert severity="error">
					<Mui.Typography>{snackbar.message}</Mui.Typography>
				</MuiAlert>
			</Mui.Snackbar>
		</DashboardLayout>
	);
};

const ReportsSavedSearches = withStyles(ReportsSavedSearchesBase, styles);

const ReportsSavedSearchesPageBase: React.FC<Props> = (props) => {
	if (!coldFusionReplatform) {
		return <ColdFusionFallback {...props} />;
	}
	return <ReportsSavedSearches {...props} />;
};

export const ReportsSavedSearchesPage = withStyles(ReportsSavedSearchesPageBase, styles);
